import { createTheme } from '@mui/material/styles';

const getTheme = (mode) => {
  const isDark = mode === 'dark';

  return createTheme({
    palette: {
      mode,
      primary: {
        main: isDark ? '#778DA9' : '#415A77',
        light: isDark ? '#E0E1DD' : '#778DA9',
        dark: '#415A77',
      },
      secondary: {
        main: isDark ? '#E0E1DD' : '#1B263B',
        light: isDark ? '#FFFFFF' : '#415A77',
        dark: isDark ? '#778DA9' : '#0D1B2A',
      },
      background: {
        default: isDark ? '#0D1B2A' : '#F5F5F0',
        paper: isDark ? '#1B263B' : '#FFFFFF',
      },
      text: {
        primary: isDark ? '#E0E1DD' : '#0D1B2A',
        secondary: isDark ? '#778DA9' : '#415A77',
      },
    },
    // Custom palette values accessible via theme.custom
    custom: {
      gradientPrimary: isDark
        ? 'linear-gradient(135deg, #415A77, #E0E1DD)'
        : 'linear-gradient(135deg, #415A77, #778DA9)',
      gradientButton: isDark
        ? 'linear-gradient(135deg, #415A77 0%, #778DA9 100%)'
        : 'linear-gradient(135deg, #415A77 0%, #778DA9 100%)',
      gradientButtonHover: isDark
        ? 'linear-gradient(135deg, #1B263B 0%, #415A77 100%)'
        : 'linear-gradient(135deg, #0D1B2A 0%, #415A77 100%)',
      cardBg: isDark
        ? 'rgba(27, 38, 59, 0.8)'
        : 'rgba(255, 255, 255, 0.85)',
      cardBorder: isDark
        ? 'rgba(65, 90, 119, 0.15)'
        : 'rgba(65, 90, 119, 0.12)',
      subtleBg: isDark
        ? 'rgba(65, 90, 119, 0.08)'
        : 'rgba(65, 90, 119, 0.05)',
      subtleBorder: isDark
        ? 'rgba(65, 90, 119, 0.1)'
        : 'rgba(65, 90, 119, 0.08)',
      hoverBg: isDark
        ? 'rgba(65, 90, 119, 0.08)'
        : 'rgba(65, 90, 119, 0.06)',
      chipBg: isDark
        ? 'rgba(65, 90, 119, 0.15)'
        : 'rgba(65, 90, 119, 0.1)',
      inputBg: isDark
        ? 'rgba(65, 90, 119, 0.04)'
        : 'rgba(65, 90, 119, 0.03)',
      inputBorder: isDark
        ? 'rgba(65, 90, 119, 0.2)'
        : 'rgba(65, 90, 119, 0.15)',
      inputBorderHover: isDark
        ? 'rgba(65, 90, 119, 0.4)'
        : 'rgba(65, 90, 119, 0.3)',
      glowPrimary: isDark
        ? 'rgba(65, 90, 119, 0.15)'
        : 'rgba(65, 90, 119, 0.1)',
      glowSecondary: isDark
        ? 'rgba(119, 141, 169, 0.1)'
        : 'rgba(65, 90, 119, 0.06)',
      shadow: isDark
        ? '0 12px 40px rgba(65, 90, 119, 0.15)'
        : '0 12px 40px rgba(13, 27, 42, 0.08)',
      shadowLg: isDark
        ? '0 16px 48px rgba(65, 90, 119, 0.12)'
        : '0 16px 48px rgba(13, 27, 42, 0.06)',
      navBg: isDark
        ? 'rgba(13, 27, 42, 0.95)'
        : 'rgba(245, 245, 240, 0.95)',
      footerBg: isDark
        ? 'rgba(13, 27, 42, 0.6)'
        : 'rgba(27, 38, 59, 0.04)',
      mockupBg: isDark
        ? 'linear-gradient(180deg, #1B263B 0%, #0D1B2A 100%)'
        : 'linear-gradient(180deg, #1B263B 0%, #0D1B2A 100%)',
      mockupBorder: isDark
        ? 'rgba(65, 90, 119, 0.3)'
        : 'rgba(65, 90, 119, 0.4)',
      mockupShadow: isDark
        ? '0 20px 60px rgba(65, 90, 119, 0.15)'
        : '0 20px 60px rgba(13, 27, 42, 0.12)',
      mockupRowBg: isDark
        ? 'rgba(65, 90, 119, 0.1)'
        : 'rgba(65, 90, 119, 0.15)',
      orb1: isDark ? '#415A77' : '#415A77',
      orb2: isDark ? '#778DA9' : '#778DA9',
      orb3: isDark ? '#E0E1DD' : '#0D1B2A',
      statBg: isDark
        ? 'rgba(65, 90, 119, 0.06)'
        : 'rgba(65, 90, 119, 0.04)',
      statBorder: isDark
        ? '1px solid rgba(65, 90, 119, 0.1)'
        : '1px solid rgba(65, 90, 119, 0.08)',
      sectionTint: isDark
        ? 'linear-gradient(180deg, rgba(65, 90, 119, 0.03) 0%, transparent 100%)'
        : 'linear-gradient(180deg, rgba(65, 90, 119, 0.02) 0%, transparent 100%)',
      highlightGradient: isDark
        ? 'linear-gradient(135deg, rgba(65, 90, 119, 0.15) 0%, rgba(119, 141, 169, 0.1) 100%)'
        : 'linear-gradient(135deg, rgba(65, 90, 119, 0.08) 0%, rgba(119, 141, 169, 0.05) 100%)',
      productCardBg: isDark
        ? 'linear-gradient(135deg, rgba(65, 90, 119, 0.08) 0%, rgba(119, 141, 169, 0.06) 100%)'
        : 'linear-gradient(135deg, rgba(65, 90, 119, 0.04) 0%, rgba(119, 141, 169, 0.03) 100%)',
      heroGlow1: isDark
        ? 'radial-gradient(circle, rgba(65, 90, 119, 0.15) 0%, transparent 70%)'
        : 'radial-gradient(circle, rgba(65, 90, 119, 0.08) 0%, transparent 70%)',
      heroGlow2: isDark
        ? 'radial-gradient(circle, rgba(119, 141, 169, 0.1) 0%, transparent 70%)'
        : 'radial-gradient(circle, rgba(119, 141, 169, 0.05) 0%, transparent 70%)',
      heroCircleBg: isDark
        ? 'linear-gradient(135deg, rgba(65, 90, 119, 0.2) 0%, rgba(119, 141, 169, 0.15) 100%)'
        : 'linear-gradient(135deg, rgba(65, 90, 119, 0.1) 0%, rgba(119, 141, 169, 0.08) 100%)',
      heroCircleBorder: isDark
        ? '1px solid rgba(65, 90, 119, 0.2)'
        : '1px solid rgba(65, 90, 119, 0.12)',
      divider: isDark
        ? 'rgba(65, 90, 119, 0.08)'
        : 'rgba(65, 90, 119, 0.06)',
      // Mock card gradients for the phone mockup
      mockCard1: 'linear-gradient(135deg, #415A77, #778DA9)',
      mockCard2: isDark
        ? 'linear-gradient(135deg, #778DA9, #E0E1DD)'
        : 'linear-gradient(135deg, #778DA9, #E0E1DD)',
      mockCard3: isDark
        ? 'linear-gradient(135deg, #1B263B, #415A77)'
        : 'linear-gradient(135deg, #0D1B2A, #415A77)',
      // Feature icon gradients
      featureGradients: [
        'linear-gradient(135deg, #415A77, #778DA9)',
        isDark
          ? 'linear-gradient(135deg, #778DA9, #E0E1DD)'
          : 'linear-gradient(135deg, #778DA9, #415A77)',
        isDark
          ? 'linear-gradient(135deg, #0D1B2A, #415A77)'
          : 'linear-gradient(135deg, #1B263B, #778DA9)',
        'linear-gradient(135deg, #1B263B, #778DA9)',
        isDark
          ? 'linear-gradient(135deg, #415A77, #E0E1DD)'
          : 'linear-gradient(135deg, #415A77, #778DA9)',
        'linear-gradient(135deg, #778DA9, #415A77)',
      ],
      // Skeleton bars in mockup
      skelBar: isDark ? 'rgba(255,255,255,0.3)' : 'rgba(255,255,255,0.5)',
      skelBarSm: isDark ? 'rgba(255,255,255,0.15)' : 'rgba(255,255,255,0.3)',
    },
    typography: {
      fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
      h1: {
        fontWeight: 800,
        fontSize: '3.5rem',
        lineHeight: 1.2,
        '@media (max-width:600px)': {
          fontSize: '2.2rem',
        },
      },
      h2: {
        fontWeight: 700,
        fontSize: '2.5rem',
        lineHeight: 1.3,
        '@media (max-width:600px)': {
          fontSize: '1.8rem',
        },
      },
      h3: {
        fontWeight: 600,
        fontSize: '1.75rem',
        lineHeight: 1.4,
      },
      h5: {
        fontWeight: 600,
        fontSize: '1.25rem',
      },
      h6: {
        fontWeight: 600,
        fontSize: '1.1rem',
      },
      body1: {
        fontSize: '1.05rem',
        lineHeight: 1.7,
      },
      body2: {
        fontSize: '0.95rem',
        lineHeight: 1.6,
      },
    },
    shape: {
      borderRadius: 12,
    },
    components: {
      MuiButton: {
        styleOverrides: {
          root: {
            textTransform: 'none',
            fontWeight: 600,
            borderRadius: 30,
            padding: '10px 28px',
            fontSize: '1rem',
          },
          containedPrimary: {
            color: '#FFFFFF',
            background: isDark
              ? 'linear-gradient(135deg, #415A77 0%, #778DA9 100%)'
              : 'linear-gradient(135deg, #415A77 0%, #778DA9 100%)',
            '&:hover': {
              background: isDark
                ? 'linear-gradient(135deg, #1B263B 0%, #415A77 100%)'
                : 'linear-gradient(135deg, #0D1B2A 0%, #415A77 100%)',
            },
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            background: isDark
              ? 'rgba(27, 38, 59, 0.8)'
              : 'rgba(255, 255, 255, 0.85)',
            backdropFilter: 'blur(10px)',
            border: isDark
              ? '1px solid rgba(65, 90, 119, 0.15)'
              : '1px solid rgba(65, 90, 119, 0.12)',
          },
        },
      },
    },
  });
};

export default getTheme;
